import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  visibleOperatorCommands,
  type DeviceSummary,
  type OperatorDeviceCommandType,
} from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { api, ApiRequestError } from '../../lib/api';
import { DeviceCommandStatus } from './DeviceCommandStatus';

const ACTION_LABEL_KEY = {
  SYNC_NOW: 'fleet.commands.syncNow',
  CLEAR_CACHE: 'fleet.commands.clearCache',
  RESTART_PLAYER: 'fleet.commands.restartPlayer',
} as const satisfies Record<OperatorDeviceCommandType, string>;

const ACTION_CONFIRM_KEY: Partial<
  Record<OperatorDeviceCommandType, 'fleet.confirm.clearCache' | 'fleet.confirm.restartPlayer'>
> = {
  CLEAR_CACHE: 'fleet.confirm.clearCache',
  RESTART_PLAYER: 'fleet.confirm.restartPlayer',
};

type DeviceRemoteActionsProps = {
  organisationId: string;
  device: DeviceSummary;
  /** Called after a command is successfully enqueued, so a caller (e.g. an open history panel) can refresh. */
  onEnqueued?: () => void;
};

type ActiveCommand = {
  commandId: string;
  type: OperatorDeviceCommandType;
};

type PendingKey = {
  type: OperatorDeviceCommandType;
  idempotencyKey: string;
};

/**
 * Operator recovery commands only (no live view / keys / power / screenshot / logs).
 * Delivery is the existing sync poll + ack queue. Status is tracked via the
 * enqueue response's command id and polled by DeviceCommandStatus — queuing
 * alone is never presented as success.
 *
 * Buttons are gated by reported capabilities (ADR 0006). Legacy devices that
 * never report capabilities still see the full V1 operator set.
 */
export function DeviceRemoteActions({ organisationId, device, onEnqueued }: DeviceRemoteActionsProps) {
  const { t } = useTranslation('displays');
  const [active, setActive] = useState<ActiveCommand | null>(null);
  const [enqueueError, setEnqueueError] = useState<string | null>(null);
  const revoked = device.status === 'revoked';
  const commands = visibleOperatorCommands(device.capabilities);

  // Holds the idempotency key for the in-flight or last-ambiguously-failed
  // action, so a double-submission or a retry after a network/5xx failure
  // reuses it. Cleared on confirmed success or a definitive 4xx, so the
  // next deliberate action gets a fresh key. Never rendered in the UI.
  const pendingKeyRef = useRef<PendingKey | null>(null);

  const enqueue = useMutation({
    mutationFn: (variables: { type: OperatorDeviceCommandType; idempotencyKey: string }) =>
      api.sendDeviceCommand(
        organisationId,
        device.id,
        { type: variables.type },
        variables.idempotencyKey,
      ),
    onSuccess: (command, variables) => {
      setEnqueueError(null);
      setActive({ commandId: command.id, type: variables.type });
      pendingKeyRef.current = null;
      onEnqueued?.();
    },
    onError: (error) => {
      setActive(null);
      setEnqueueError(apiErrorMessage(error, t('fleet.queueError')));
      // A definitive 4xx (validation, conflict, etc.) means the server
      // resolved the request; the next deliberate action should get a new
      // key. A network-level failure or 5xx is ambiguous -- keep the key
      // so a manual retry of this same action reuses it.
      const isDefinitive4xx =
        error instanceof ApiRequestError && error.status >= 400 && error.status < 500;
      if (isDefinitive4xx) {
        pendingKeyRef.current = null;
      }
    },
  });

  const run = (type: OperatorDeviceCommandType) => {
    const confirmKey = ACTION_CONFIRM_KEY[type];
    const confirmMessage = confirmKey ? t(confirmKey, { code: device.deviceCode }) : null;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setEnqueueError(null);
    const reuseKey = pendingKeyRef.current?.type === type ? pendingKeyRef.current.idempotencyKey : null;
    const idempotencyKey = reuseKey ?? crypto.randomUUID();
    pendingKeyRef.current = { type, idempotencyKey };
    enqueue.mutate({ type, idempotencyKey });
  };

  const busy = enqueue.isPending;

  if (commands.length === 0) {
    return (
      <p className="text-xs text-muted">{t('fleet.noCommands')}</p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {commands.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => run(type)}
            disabled={revoked || busy}
            className="rounded-lg border border-line px-2 py-1 text-xs font-semibold text-ink disabled:opacity-40"
          >
            {t(ACTION_LABEL_KEY[type])}
          </button>
        ))}
      </div>
      {enqueueError && <p className="mt-1 text-xs text-red-600">{enqueueError}</p>}
      {active && (
        <DeviceCommandStatus
          organisationId={organisationId}
          deviceId={device.id}
          commandId={active.commandId}
          commandType={active.type}
        />
      )}
    </div>
  );
}
